import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import { colorsByStatus, titleByRuleType } from '@front/common/src/forms/validators/password/utils';
import { useValidatedPassword } from '@front/common/src/forms/validators/password/use-validated-password';
import {
  PasswordRuleProps,
  PasswordRuleType,
  PasswordStatus,
} from '@front/common/src/forms/validators/password/type';

import { translate } from '@front/i18n';

import Title from '../base-text/variants/title';
import Text from '../base-text/variants/text';
import Icon from '../icon/icon';
import { minWidth } from '../../styles/mixins';

import InputPasswordLegacy, { InputProps } from './input-password-legacy';

const t = translate('components.validated_password');

const StyledRule = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    text-align: center;
  }
`;

const StyledIcon = styled(Icon)`
  margin-bottom: 2.5px;
`;

const RulesContainer = styled.div`
  margin-top: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  ${minWidth.md`
    gap: 10px;
  `};
`;

const RulesBlock = styled.div`
  gap: 7px;
  margin: -10px 0 15px 15px;
`;

const InputContainer = styled.div`
  position: relative;
`;

const Rule = ({ status = 'stale', ruleType }: PasswordRuleProps) => (
  <StyledRule>
    <div>
      {status === 'valid' ? (
        <StyledIcon dataTestId={`checkmark-${ruleType}`} name='check-circle' size={15} color={colorsByStatus.valid} />
      ) : (
        <Title.SM color={colorsByStatus[status]}>{titleByRuleType[ruleType]}</Title.SM>
      )}
    </div>
    <Text.XSMedium color={colorsByStatus[status]}>{t(`.${ruleType}`)}</Text.XSMedium>
  </StyledRule>
);

type ValidatedPasswordProps = InputProps & { value?: string; touched?: boolean; onError?: (error: string) => void };

export const ValidatedPassword = React.forwardRef<HTMLInputElement, ValidatedPasswordProps>(
  ({ value, touched, error, onChange, onFocus, onError, ...rest }, ref) => {
    const {
      state,
      invalidateState,
      onChange: internalOnChange,
      onFocus: internalOnFocus,
      isFocused,
      setBlurred,
      isRulesVisible,
      areAllRequirementsMet,
    } = useValidatedPassword(value);
    const stateRef = useRef<Record<PasswordRuleType, PasswordStatus>>(state);

    const handleOnError = useCallback(
      (newState: Record<PasswordRuleType, PasswordStatus>) => {
        Object.entries(newState).forEach(([rule, ruleValue]) => {
          if (ruleValue === 'invalid' && stateRef.current[rule] !== 'invalid') {
            onError?.(rule);
          }
        });
        stateRef.current = newState;
      },
      [onError],
    );

    useEffect(() => {
      if (touched && error && !isFocused) invalidateState(handleOnError);
    }, [error, touched, invalidateState, isFocused, handleOnError]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      internalOnChange(e.target.value);
      onChange?.(e);
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      internalOnFocus();
      onFocus?.(e);
    };

    const handleBlur = () => {
      setTimeout(() => {
        setBlurred();
        invalidateState(handleOnError);
      }, 200);
    };
    const displayRules = !!(isRulesVisible || (touched && error));
    return (
      <>
        <InputContainer>
          <InputPasswordLegacy
            value={value}
            touched={touched}
            error={error}
            {...rest}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            ref={ref}
            isErrorDisplay={false}
            isBorderErrorWithoutErrorDisplay={displayRules && !areAllRequirementsMet}
          />
        </InputContainer>
        {displayRules && (
          <RulesBlock data-testid='rulesBolck'>
            <Text.XSMedium color={colorsByStatus.stale}>{t('.text')}</Text.XSMedium>
            <RulesContainer>
              <Rule status={state.upperCase} ruleType='upperCase' />
              <Rule status={state.lowerCase} ruleType='lowerCase' />
              <Rule status={state.number} ruleType='number' />
              <Rule status={state.symbol} ruleType='symbol' />
              <Rule status={state.length} ruleType='length' />
            </RulesContainer>
          </RulesBlock>
        )}
      </>
    );
  },
);
